import { useEffect, useState, useCallback } from 'react'
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, TextField,
  Button, Stack, IconButton, CircularProgress, Alert, InputAdornment, Tooltip, TablePagination,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, FormControlLabel, Checkbox, Grid,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import RefreshIcon from '@mui/icons-material/Refresh'
import FilterListIcon from '@mui/icons-material/FilterList'
import EntityAutocomplete from './EntityAutocomplete'
import { useNotify } from './Notifications'

const getPath = (obj, path) => path.split('.').reduce((o, k) => (o == null ? o : o[k]), obj)

// ABM genérico: lista + buscador + diálogo de alta/edición.
// Config: { title, api:{buscar,crear,actualizar,eliminar}, columns, fields, toPayload, fromEntity,
//           canCreate, canEdit, canDelete, searchable, initialSearch, searchPlaceholder }
export default function CrudPage({
  title, api, columns, fields = [], toPayload = (f) => f, fromEntity = (e) => ({ ...e }),
  canCreate = true, canEdit = true, canDelete = false, searchable = true,
  initialSearch = '', searchPlaceholder = 'Buscar…', readOnly = false,
}) {
  const notify = useNotify()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState(initialSearch)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [showFilters, setShowFilters] = useState(false)
  const [colFilters, setColFilters] = useState({})

  // Filtro por columna (client-side) sobre las filas cargadas.
  const setColFilter = (key, val) => { setColFilters((f) => ({ ...f, [key]: val })); setPage(0) }
  const filteredRows = rows.filter((row) =>
    columns.every((c) => {
      const f = colFilters[c.key]
      if (!f) return true
      const val = c.filterValue ? c.filterValue(row) : getPath(row, c.key)
      return String(val ?? '').toLowerCase().includes(f.toLowerCase())
    }),
  )

  const cargar = useCallback(async (texto = search) => {
    setLoading(true); setError(null)
    try {
      const list = api.buscar ? await api.buscar(texto) : await api.listar()
      setRows(list)
      setPage(0)
    } catch (e) {
      setError('No se pudieron cargar los datos.')
      setRows([])
    } finally { setLoading(false) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api])

  useEffect(() => { cargar(initialSearch) }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const abrirNuevo = () => { setEditing(null); setForm({}); setDialogOpen(true) }
  const abrirEditar = (row) => { setEditing(row); setForm(fromEntity(row)); setDialogOpen(true) }
  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }))

  const guardar = async () => {
    const faltan = fields.filter((fl) => fl.required && (form[fl.name] == null || form[fl.name] === ''))
    if (faltan.length) { notify.error(`Completá: ${faltan.map((f) => f.label).join(', ')}`); return }
    setSaving(true)
    try {
      const payload = toPayload(form, editing)
      if (editing) await api.actualizar(editing.id, payload)
      else await api.crear(payload)
      notify.success(editing ? 'Actualizado.' : 'Creado.')
      setDialogOpen(false)
      cargar()
    } catch (e) {
      notify.error(e?.response?.data?.[0]?.message || e?.response?.data?.message || 'No se pudo guardar.')
    } finally { setSaving(false) }
  }

  const eliminar = async (row) => {
    if (!window.confirm(`¿Eliminar "${getPath(row, columns[0]?.key) ?? row.id}"?`)) return
    try { await api.eliminar(row.id); notify.success('Eliminado.'); cargar() }
    catch (e) { notify.error(e?.response?.data?.[0]?.message || 'No se pudo eliminar.') }
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h5" fontWeight={600}>{title}</Typography>
        {canCreate && !readOnly && (
          <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={abrirNuevo}>Nuevo</Button>
        )}
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2}>
          {searchable && (
            <TextField size="small" fullWidth placeholder={searchPlaceholder} value={search}
              onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && cargar(search)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          )}
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => cargar(search)} disabled={loading}>
            {searchable ? 'Buscar' : 'Actualizar'}
          </Button>
          <Tooltip title="Filtrar por columna">
            <Button variant={showFilters ? 'contained' : 'outlined'} color={showFilters ? 'secondary' : 'inherit'}
              startIcon={<FilterListIcon />}
              onClick={() => { if (showFilters) setColFilters({}); setShowFilters((s) => !s) }}>
              Filtros
            </Button>
          </Tooltip>
        </Stack>
      </Paper>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                {columns.map((c) => <TableCell key={c.key} align={c.align}>{c.label}</TableCell>)}
                {(canEdit || canDelete) && !readOnly && <TableCell align="center" width={110}>Acciones</TableCell>}
              </TableRow>
              {showFilters && (
                <TableRow>
                  {columns.map((c) => (
                    <TableCell key={c.key} sx={{ py: 0.5 }}>
                      {c.filterable === false ? null : (
                        <TextField variant="standard" size="small" fullWidth placeholder="Filtrar…"
                          value={colFilters[c.key] || ''} onChange={(e) => setColFilter(c.key, e.target.value)} />
                      )}
                    </TableCell>
                  ))}
                  {(canEdit || canDelete) && !readOnly && <TableCell />}
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 && (
                <TableRow><TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4, color: 'text.secondary' }}>Sin registros.</TableCell></TableRow>
              )}
              {filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                <TableRow key={row.id} hover>
                  {columns.map((c) => (
                    <TableCell key={c.key} align={c.align}>{c.render ? c.render(row) : (getPath(row, c.key) ?? '—')}</TableCell>
                  ))}
                  {(canEdit || canDelete) && !readOnly && (
                    <TableCell align="center">
                      {canEdit && <Tooltip title="Editar"><IconButton size="small" onClick={() => abrirEditar(row)}><EditIcon fontSize="small" /></IconButton></Tooltip>}
                      {canDelete && <Tooltip title="Eliminar"><IconButton size="small" onClick={() => eliminar(row)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {!loading && filteredRows.length > 0 && (
          <TablePagination
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Editar ${title}` : `Nuevo ${title}`}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {fields.map((f) => (
              <Grid item xs={12} sm={f.full ? 12 : 6} key={f.name}>
                <FieldInput field={f} value={form[f.name]} onChange={(v) => setField(f.name, v)} />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="secondary" onClick={guardar} disabled={saving}>
            {saving ? <CircularProgress size={22} color="inherit" /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

function FieldInput({ field, value, onChange }) {
  const { type = 'text', label, options, optionLabel = (o) => o?.descripcion || o?.nombre, fetcher, required } = field
  if (type === 'checkbox') {
    return <FormControlLabel control={<Checkbox checked={!!value} onChange={(e) => onChange(e.target.checked)} />} label={label} />
  }
  if (type === 'select') {
    return (
      <TextField select fullWidth size="small" label={label} required={required}
        value={value?.id ?? value ?? ''} onChange={(e) => onChange((options || []).find((o) => o.id === e.target.value) ?? e.target.value)}>
        {(options || []).map((o) => <MenuItem key={o.id ?? o} value={o.id ?? o}>{optionLabel(o) ?? o}</MenuItem>)}
      </TextField>
    )
  }
  if (type === 'autocomplete') {
    return <EntityAutocomplete label={label} value={value} onChange={onChange} fetcher={fetcher} getOptionLabel={optionLabel} required={required} minChars={field.minChars ?? 2} />
  }
  return (
    <TextField fullWidth size="small" type={type} label={label} required={required}
      value={value ?? ''} onChange={(e) => onChange(type === 'number' ? e.target.value : e.target.value)}
      InputLabelProps={type === 'date' ? { shrink: true } : undefined} />
  )
}
